import React from "react";
import { Check } from "lucide-react";

import { useAgents, usePatchAgent } from "@/shared/api/agents";
import { ListCard, ListRow } from "@/shared/components/ListCard";
import { PrimaryButton } from "@/shared/components/settings-atoms";
import { Switch } from "@/shared/components/ui/switch";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { extensionLink } from "../shareLinks";
import { InterfaceDialog } from "./InterfaceDialog";
import { LinkField } from "@/shared/components/LinkField";
import { LinkedAppsWarning } from "./LinkedAppsWarning";

import type { Visibility } from "@/shared/types";
import type { SetupProps } from "../types";

/**
 * The agent in the Definable extension: a switch puts it in the extension's list of agents,
 * applied at once. Who finds it there is its visibility, set on Onboarding; the link opens it
 * in the extension straight away.
 */
export function ExtensionSetup({ agent, onClose }: SetupProps) {
  const meta = catalogFor("extension")!;
  const row = useAgents().data?.find((each) => each.id === agent.id);
  const patch = usePatchAgent();
  const listed = !!row?.extension_listed;

  return (
    <InterfaceDialog
      meta={meta}
      title="Chrome extension"
      description={meta.desc}
      onClose={onClose}
      footer={<PrimaryButton type="button" onClick={onClose}>Done</PrimaryButton>}
    >
      <ListCard>
        <ListRow title="Show in the Definable extension" desc={`${agent.name} in its list of agents`}>
          <Switch
            checked={listed}
            disabled={!row || patch.isPending}
            onCheckedChange={(on) => patch.mutate({ id: agent.id, body: { extension_listed: on } })}
            aria-label="Show in the Definable extension"
          />
        </ListRow>
      </ListCard>
      {listed && row && <WhoFindsIt visibility={row.visibility} />}
      <div className="space-y-1.5">
        <div className="text-[12.5px] font-medium text-[var(--text-secondary)]">Link that opens it in the extension</div>
        <LinkField url={extensionLink(agent.id)} />
      </div>
      <LinkedAppsWarning agent={agent} />
    </InterfaceDialog>
  );
}

/** Where people see the agent in the extension's list, by its visibility. */
function WhoFindsIt({ visibility }: { visibility: Visibility }) {
  const rows: [string, string, boolean][] = [
    ["Your teammates", "Under Your workspace", true],
    ["People it invites", "Under Recent, once they open it", visibility === "private"],
    ["Anyone who used it or has its link", "Under Recent", visibility !== "private"],
    ["Everyone", "Under Discover", visibility === "public"],
  ];
  return (
    <ListCard label="Who finds it there">
      {rows.filter(([, , on], i) => on || i === 3).map(([title, where, on]) => (
        <ListRow key={title} title={title} desc={on ? where : `${where}, once it's public`} muted={!on}>
          {on ? <Check className="w-4 h-4 text-[var(--success)]" strokeWidth={2} /> : <span className="text-[12px] text-[var(--text-quaternary)]">Not yet</span>}
        </ListRow>
      ))}
    </ListCard>
  );
}
